import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO/SEO';

const PhilosophyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="経営理念・代表メッセージ｜塚本祥太郎"
        description="SrWith株式会社 代表取締役 塚本祥太郎のメッセージ。「今日よりも明日の方がいい会社を5万社つくる」をビジョンに、社労士3代目として17年以上の実務経験から生まれた労務プラットフォームへの想い。"
      />

      {/* Google Fonts for Klee One */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Klee+One:wght@400;600&display=swap');
        .font-handwriting {
          font-family: 'Klee One', cursive;
        }
      `}</style>
      
      {/* Hero Section - 大胆なタイポグラフィと非対称レイアウト */}
      <section className="relative overflow-hidden bg-gradient-to-br from-pink-beige to-white">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-48 translate-x-48 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full translate-y-40 -translate-x-40 blur-2xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 items-center min-h-[60vh] lg:min-h-[70vh]">
            
            {/* Left Content - PC版のみ */}
            <div className="hidden lg:block lg:col-span-7 space-y-8 lg:space-y-10 text-center lg:text-left">
              <div className="space-y-4 lg:space-y-6">
                <div className="relative">
                  <span className="text-xs sm:text-sm font-medium text-brown tracking-wide">
                    <span className="font-medium whitespace-nowrap">SrWith</span>株式会社 代表取締役
                  </span>
                  <div className="mt-2 w-12 h-px bg-brown mx-auto lg:mx-0"></div>
                </div>
                
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-brown leading-[0.9] tracking-tight">
                  塚本祥太郎
                </h1>
              </div>
              
              <div className="relative">
                <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-pink-beige to-accent rounded-full hidden lg:block"></div>
                <blockquote className="lg:pl-8 space-y-4">
                  <p className="text-lg sm:text-xl md:text-3xl font-semibold text-brown leading-relaxed">
                    今日よりも明日の方がいい会社を<span className="text-accent">５万社</span>つくる
                  </p>
                </blockquote>
              </div>
            </div>
            
            {/* Right Image - モバイル版では文字オーバーレイ */}
            <div className="col-span-1 lg:col-span-5 order-first lg:order-last">
              <div className="relative ml-0 lg:ml-8 max-w-sm mx-auto lg:max-w-none">
                <div className="aspect-[4/5] relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-beige/20 to-transparent rounded-[2.5rem] transform rotate-2"></div>
                  <div className="relative bg-white rounded-[2.5rem] overflow-hidden shadow-2xl transform rotate-0 lg:-rotate-1 lg:hover:rotate-0 transition-transform duration-700">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/representative.png`}
                      alt="塚本祥太郎 代表取締役"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-full items-center justify-center text-gray-400 bg-gray-50">
                      代表者写真
                    </div>
                    
                    {/* モバイル版文字オーバーレイ */}
                    <div className="lg:hidden absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex flex-col justify-end p-6">
                      <div className="text-center text-white space-y-3">
                        <div>
                          <span className="text-xs font-medium tracking-wide">
                            <span className="font-medium whitespace-nowrap">SrWith</span>株式会社 代表取締役
                          </span>
                        </div>
                        
                        <h1 className="text-3xl font-black leading-tight">
                          塚本祥太郎
                        </h1>
                        
                        <p className="text-sm font-semibold leading-relaxed">
                          今日よりも明日の方がいい会社を<span className="text-pink-beige">５万社</span>つくる
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Stats */}
                <div className="absolute -bottom-4 sm:-bottom-6 -left-2 sm:-left-4 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl border border-gray-100 lg:block hidden">
                  <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">経験年数</div>
                  <div className="text-xl sm:text-2xl font-bold text-accent">17年+</div>
                </div>
                
                <div className="absolute -top-4 sm:-top-6 -right-2 sm:-right-4 bg-pink-beige rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl text-white w-24 sm:w-28 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide opacity-90">支援企業</div>
                  <div className="text-xl sm:text-2xl font-bold">150社以上</div>
                </div>
                
                {/* スマホ版：経験年数を支援企業の下に配置 */}
                <div className="lg:hidden absolute top-16 sm:top-20 -right-2 sm:-right-4 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl border border-gray-100 w-24 sm:w-28 text-center">
                  <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">経験年数</div>
                  <div className="text-xl sm:text-2xl font-bold text-accent">17年+</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 代表メッセージセクション - 手紙らしいレイアウト */}
      <section className="py-16 sm:py-20 lg:py-24" style={{backgroundColor: '#f5f1f0'}}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-brown mb-4 lg:mb-6 leading-tight">
              代表メッセージ
            </h2>
            <div className="w-16 h-px bg-pink-beige mx-auto"></div>
          </div>

          {/* 手紙レイアウト */}
          <div className="max-w-2xl mx-auto" style={{backgroundColor: '#faf8f7', padding: '2rem', borderRadius: '8px'}}>
            
            {/* 日付 */}
            <div className="text-right mb-8 lg:mb-12 text-brown text-sm">
              2025年8月
            </div>
            
            {/* 手紙本文 */}
            <div className="space-y-4 lg:space-y-6 text-base lg:text-lg font-handwriting text-gray-700" style={{
              lineHeight: '1.8rem',
              backgroundImage: 'repeating-linear-gradient(transparent, transparent 1.6rem, rgba(216, 197, 201, 0.15) 1.6rem, rgba(216, 197, 201, 0.15) calc(1.6rem + 1px))',
              backgroundSize: '100% 1.8rem'
            }}>
              
              <p className="text-left">
                私が社労士となり早17年が経過しました。
              </p>
              
              <p className="text-left">
                これまで顧問先約150社以上に対し、社労士としての伝統的な業務として社保・労保手続き、就業規則作成、各種助成金申請代行等を行い、また、３０代前半ごろから３号業務＋コンサル業務を行ってきました。
              </p>
              
              <p className="text-left">
                その中で、「もっと社労士が企業の根幹に携わる業務ができないのか」「なぜ業界向けシステムは社保や労保手続きが主なシステムばかりなのだろう。コンサルしやすいシステムがあればいいのに。」「社労士は優秀で、もっと顧問先に貢献できるのに…」このようなことを考えていました。
              </p>
              
              <p className="text-left">
                私は収入だけで言えば社労士業務だけできっと食べていけるでしょう。
              </p>
              
              <p className="text-center text-lg lg:text-xl font-semibold text-brown my-6 lg:my-8">
                でも、本当にそれでいいのか。
              </p>
              
              <p className="text-left">
                社労士3代目という贅沢な環境にいるからこそ、また、40歳で15年以上の業務経験があるからこそ、誰よりも頑張らなければいけないと考えています。
              </p>
              
              <p className="text-left">
                企業にもっと貢献ができるのにチャンスがない、軽く扱われる、事務屋だと思われる、単価を低く見積られる、コンサル契約に移行しにくい...そんな経験から、社労士がもっと活躍できる場を作りたい、社労士は皆優秀なのに！そのような気持ちからスタートしたプロジェクトです。
              </p>
              
              <p className="text-left">
                当事務所は私の祖父の代から社労士業を営んでいるため、私の血と肉は社労士からできていると言っても過言ではありません。
              </p>
              
              <p className="text-center text-lg lg:text-xl font-semibold text-brown my-6 lg:my-8">
                私は社労士という資格が好きです。
              </p>
              
              <p className="text-left">
                だからこそ業界が今よりも一歩先に進むことを心から祈っています。
              </p>
              
              <p className="text-center text-base lg:text-lg font-semibold text-brown my-8 lg:my-10">
                「今日よりも明日の方が良い会社を５万社つくる！」
              </p>
              
              <p className="text-left">
                これが私のビジョンです。一人では到底到達できません。<br />
                ぜひ、この活動に興味を持って頂ける方の御参加を心からお待ちしています。
              </p>
              
              {/* 署名 */}
              <div className="text-right pt-8 lg:pt-12 mt-8 lg:mt-12">
                <div className="inline-block text-right space-y-1">
                  <div className="text-sm lg:text-base text-brown"><span className="font-medium whitespace-nowrap">SrWith</span>株式会社</div>
                  <div className="text-sm lg:text-base text-brown">代表取締役</div>
                  <div className="text-base lg:text-lg font-semibold text-brown">塚本 祥太郎</div>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      </section>

      {/* Profile Section - 信頼性のある情報 */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-brown mb-4 lg:mb-6">
              これまでの歩み
            </h2>
            <div className="w-16 h-px bg-pink-beige mx-auto"></div>
          </div>

          <div className="space-y-16 lg:space-y-20">
            
            {/* Career */}
            <div className="space-y-6 lg:space-y-8">
              <h3 className="text-xl sm:text-2xl font-bold text-brown border-b border-gray-200 pb-3 lg:pb-4 text-center">
                経歴
              </h3>
              
              <div className="space-y-8 lg:space-y-12">
                <div className="relative pl-6 lg:pl-8">
                  <div className="absolute left-0 top-1 w-3 h-3 rounded-full bg-pink-beige"></div>
                  <div className="absolute left-1.5 top-4 w-px h-12 lg:h-16 bg-gray-200"></div>
                  <div>
                    <h4 className="text-base lg:text-lg font-semibold text-brown mb-2 lg:mb-3">公務員時代</h4>
                    <ul className="space-y-1 lg:space-y-2 text-gray-text font-light text-sm lg:text-base">
                      <li>23歳で社労士試験に合格</li>
                      <li>総務省　佐賀行政評価事務所 年金記録確認　佐賀第三者委員会で１年半勤務</li>
                    </ul>
                  </div>
                </div>
                
                <div className="relative pl-6 lg:pl-8">
                  <div className="absolute left-0 top-1 w-3 h-3 rounded-full bg-accent"></div>
                  <div>
                    <h4 className="text-base lg:text-lg font-semibold text-brown mb-2 lg:mb-3">独立から現在</h4>
                    <ul className="space-y-2 lg:space-y-3 text-gray-text font-light text-xs sm:text-sm leading-relaxed">
                      <li>平成20年2月<br className="lg:hidden" />　40年以上続く塚本行政書士事務所に併設する形で「塚本社会保険労務士事務所」を開業　支援先150社以上</li>
                      <li>令和２年４月<br className="lg:hidden" />　福岡コミュニティラジオ天神にて『塚本祥太郎のライフコネクト』パーソナリティ就任</li>
                      <li>令和２年６月<br className="lg:hidden" />　FMからつ　夕方チャンス木曜『ワンダフォー』パーソナリティ就任</li>
                      <li>令和５年５月<br className="lg:hidden" />　<span className="font-medium whitespace-nowrap">SrWith</span>株式会社設立　代表取締役社に就任</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Awards */}
            <div className="space-y-6 lg:space-y-8">
              <h3 className="text-xl sm:text-2xl font-bold text-brown border-b border-gray-200 pb-3 lg:pb-4 text-center">
                表彰歴
              </h3>
              
              <div className="space-y-6 lg:space-y-8">
                <div className="bg-pink-beige/10 rounded-xl lg:rounded-2xl p-4 lg:p-6 hover:bg-pink-beige/15 transition-colors duration-300">
                  <div className="text-xs lg:text-sm font-medium text-brown mb-2">令和７年７月</div>
                  <h4 className="text-lg lg:text-xl font-semibold text-brown mb-0 lg:mb-2">
                    賞金最大100万円！<br />
                    「ゼロスタビジネスコンテスト in 唐津」<br />
                    <span className="text-brown">大賞受賞</span>
                  </h4>
                </div>
                
                <div className="bg-gray-50/60 rounded-xl lg:rounded-2xl p-4 lg:p-6 hover:bg-gray-50 transition-colors duration-300">
                  <div className="text-xs lg:text-sm font-medium text-brown mb-2">令和７年３月</div>
                  <h4 className="text-lg lg:text-xl font-semibold text-brown">
                    SAGA DX Talk Live<br />
                    事業所部門受賞
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - 優しい招待 */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="space-y-8 lg:space-y-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-brown leading-tight">
              共に「いい会社」を<br />
              作りませんか？
            </h2>
            
            <p className="text-lg sm:text-xl md:text-2xl text-gray-text font-light leading-relaxed max-w-2xl mx-auto">
              <span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>は社労士の専門性と企業の成長を結ぶ架け橋です。<br />
              一緒に業界の未来を創造しましょう。
            </p>
            
            <div className="pt-6 lg:pt-8">
              <Link 
                to="/contact"
                className="inline-block bg-accent text-white px-12 py-5 rounded-2xl text-lg font-semibold hover:bg-opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                お問い合わせはこちら
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default PhilosophyPage;